import React, { useRef, useState, useEffect } from "react";
import ReactPlayer from "react-player";
import { Box, IconButton, Typography, Slider, CircularProgress } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import VideocamIcon from "@mui/icons-material/Videocam";
// Iconsax — ClusterDen's standard icon library (see [[icon-library-iconsax]]); Play/Pause
// replace the plain MUI PlayArrow/Pause triangles (Rahim, 2026-08-27: "use better icon of play").
import { Play, Pause } from "iconsax-react";
import ChatMediaCollapsedPreview from "./ChatMediaCollapsedPreview";
import { useEchoMediaFetch } from "./useEchoMediaFetch";

const VideoBox = ({ videoUrl, mediaThumbnailUrl, mediaFileSizeBytes, contactId, messageId, metaMediaId }) => {
  const { resolvedUrl, isFetching, handleFetch } = useEchoMediaFetch(videoUrl, {
    contactId,
    messageId,
    tag: "VideoBox",
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const playerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setRevealed(false);
    setIsPlaying(false);
    setShowControls(false);
    setProgress(0);
    setVideoDuration(0);
    setCurrentTime(0);
    setHasError(false);
    setIsLoading(false);
  }, [resolvedUrl, mediaThumbnailUrl]);

  if (!resolvedUrl && metaMediaId) {
    return (
      <ChatMediaCollapsedPreview
        thumbnailUrl={mediaThumbnailUrl || null}
        fallbackSrc={null}
        sizeBytes={null}
        onActivate={handleFetch}
        minHeight={150}
        maxHeight={200}
        isLoading={isFetching}
        bottomLeft={
          <>
            <VideocamIcon sx={{ fontSize: 16, opacity: 0.95 }} />
            <Typography component="span" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>Video</Typography>
          </>
        }
      />
    );
  }

  const s = typeof resolvedUrl === "string" ? resolvedUrl.trim() : "";
  const isRemote = s.startsWith("http://") || s.startsWith("https://");
  const useCollapsedGate = isRemote && !revealed;

  const sizeBytes =
    mediaFileSizeBytes != null && Number.isFinite(Number(mediaFileSizeBytes))
      ? Number(mediaFileSizeBytes)
      : null;

  const handlePlayPause = () => {
    setIsPlaying((prev) => !prev);
    if (!isPlaying) setShowControls(true);
  };

  const handleProgress = (state) => {
    setProgress(state.played * 100);
    setCurrentTime(state.playedSeconds);
  };

  const handleSeek = (e, newValue) => {
    const seekTo = (newValue / 100) * videoDuration;
    playerRef.current.seekTo(seekTo);
    setProgress(newValue);
    setCurrentTime(seekTo);
  };

  const handleFullscreen = () => {
    if (!isFullscreen) {
      setIsFullscreen(true);
      containerRef.current.requestFullscreen();
    } else {
      setIsFullscreen(false);
      document.exitFullscreen();
    }
  };

  const handleDuration = (duration) => {
    setVideoDuration(duration);
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const activateFull = () => {
    setIsLoading(true);
    setRevealed(true);
  };

  if (useCollapsedGate) {
    return (
      <ChatMediaCollapsedPreview
        thumbnailUrl={mediaThumbnailUrl || null}
        fallbackSrc={null}
        sizeBytes={sizeBytes}
        onActivate={activateFull}
        minHeight={150}
        maxHeight={200}
        bottomLeft={
          <>
            <VideocamIcon sx={{ fontSize: 16, opacity: 0.95 }} />
            <Typography component="span" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
              Video
            </Typography>
          </>
        }
      />
    );
  }

  const videoBubbleMaxWidth = "calc(var(--chatbox-width, 100vw) * 0.5)";
  const videoBubbleMinWidth = { xs: 220, sm: 260, md: 280, lg: 280, xl: 280 };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: `min(100%, ${videoBubbleMaxWidth})`,
        maxWidth: videoBubbleMaxWidth,
        minWidth: videoBubbleMinWidth,
        height: 200,
        maxHeight: 200,
        backgroundColor: "#000",
        borderRadius: "8px",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {isLoading && (
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 10 }}>
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      )}
      {hasError ? (
        <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#fff", padding: 2 }}>
          <Typography sx={{ mb: 1 }}>Failed to load video</Typography>
          <IconButton onClick={handlePlayPause} sx={{ color: "#fff", backgroundColor: "rgba(255, 255, 255, 0.2)", "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.3)" } }}>
            <Play size={18} color="currentColor" variant="Bold" />
          </IconButton>
        </Box>
      ) : (
        <ReactPlayer
          ref={playerRef}
          url={resolvedUrl}
          playing={isPlaying}
          width="100%"
          height="100%"
          config={{ file: { attributes: { preload: "metadata" } } }}
          onProgress={handleProgress}
          onDuration={handleDuration}
          onError={handleError}
          onReady={() => { setIsLoading(false); setHasError(false); }}
        />
      )}

      {!isPlaying && (
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", display: "flex", alignItems: "center", flexDirection: "column", justifyContent: "center", color: "#fff" }}>
          {/* Rahim, 2026-08-27: "i dont like the icon make it small and use better one" — the
              64px filled PlayCircle read too heavy. Back to a compact dark-circle button (same
              small footprint as before) but with Iconsax's cleaner `Play` triangle instead of
              MUI's PlayArrow. */}
          <IconButton
            onClick={handlePlayPause}
            sx={{
              color: "#fff",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
              padding: "10px",
            }}
          >
            <Play size={22} color="currentColor" variant="Bold" />
          </IconButton>
          <Typography sx={{ marginTop: "8px", fontSize: "14px" }}>{formatTime(videoDuration)}</Typography>
        </Box>
      )}

      {showControls && (
        <Box sx={{ position: "absolute", bottom: 0, left: -15, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 16px", background: "rgba(0, 0, 0, 0.6)" }}>
          <IconButton onClick={handlePlayPause} sx={{ color: "#fff" }}>
            {isPlaying ? (
              <Pause size={18} color="currentColor" variant="Bold" />
            ) : (
              <Play size={18} color="currentColor" variant="Bold" />
            )}
          </IconButton>
          <Typography sx={{ color: "#fff", fontSize: "14px", marginRight: "8px" }}>{formatTime(currentTime)}</Typography>
          <Slider value={progress} onChange={handleSeek} sx={{ flex: 1, margin: "0 8px", color: "#4caf50", "& .MuiSlider-thumb": { display: "none" } }} />
          <Typography sx={{ color: "#fff", fontSize: "14px", marginLeft: "8px" }}>{formatTime(videoDuration)}</Typography>
          <IconButton onClick={handleFullscreen} sx={{ color: "#fff" }}>
            {isFullscreen ? <CloseFullscreenIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default VideoBox;
